import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { AlertCircle, Mail, Plus } from 'lucide-react';
import { Button } from '../../components/Button.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { api } from '../onboarding/api.ts';
import { PublicLayout } from '../authorization/PublicLayout.tsx';
import { ProviderMark } from './ProviderMark.tsx';
import { LAST_PROVIDER_KEY, loginUrl } from './sessionExpiry.ts';
import styles from './SignInPage.module.css';

const ERROR_MESSAGES: Record<string, string> = {
  unavailable: 'Sign-in is temporarily unavailable. Please try again shortly.',
  provider_unavailable: 'That sign-in option is not available right now. Choose another one.',
  expired: 'Your sign-in attempt timed out. Please try again.',
  denied: 'Sign-in was cancelled or access was denied by your identity provider.',
  unverified_email:
    "Your identity provider reports an unverified email address. Please verify your email with your organization's administrator before signing in.",
  failed: 'Sign-in failed. Please try again.',
  session_expired: 'Your session ended. Sign in to continue where you were.',
};

export function SignInPage() {
  const search = useSearch({ strict: false }) as { redirect?: string; error?: string };
  const targetRedirect = search.redirect || '/';
  const errorMessage = search.error
    ? (ERROR_MESSAGES[search.error] ?? ERROR_MESSAGES.failed)
    : null;
  const [providers, setProviders] = useState<{ id: string; name: string }[]>([]);

  const signIn = (provider = '') => {
    try {
      localStorage.setItem(LAST_PROVIDER_KEY, provider);
    } catch {
      // The choice is only a convenience.
    }
    window.location.assign(loginUrl(targetRedirect, provider));
  };

  useEffect(() => {
    let live = true;
    api
      .signInProviders()
      .then((result) => {
        if (live) setProviders(result.providers);
      })
      .catch(() => {
        // Email sign-in still works through the identity provider's own page.
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <PublicLayout title="Sign in to Traceira" hideTitleHeading>
      <div className={styles.container}>
        <section className={styles.section} aria-labelledby="sign-in-title">
          <h2 id="sign-in-title" className={styles.title}>
            Sign in
          </h2>

          {errorMessage && (
            <div className={styles.errorBanner} role="alert">
              <AlertCircle size={18} aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <p className={styles.description}>
            Use your work account. Workspace access follows your verified email address.
          </p>

          <div className={styles.providers}>
            {providers.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="secondary"
                block
                onPress={() => signIn(provider.id)}
              >
                <ProviderMark id={provider.id} />
                <span>Continue with {provider.name}</span>
              </Button>
            ))}
            <Button
              type="button"
              variant={providers.length ? 'secondary' : 'primary'}
              block
              onPress={() => signIn()}
            >
              <Mail size={16} aria-hidden="true" />
              <span>Continue with email</span>
            </Button>
          </div>
        </section>

        <section className={styles.section} aria-labelledby="create-title">
          <h2 id="create-title" className={styles.subtitle}>
            New to Traceira?
          </h2>
          <p className={styles.description}>
            No account needed to start. Verify your domain, approve the authorization by email, and
            your workspace opens.
          </p>
          <Link to="/setup" search={{ step: '1' }} className={buttonClass('secondary')}>
            <Plus size={16} aria-hidden="true" />
            <span>Create a workspace</span>
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}
